import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { registrarEvento } from '../analytics';

/*
 * Botón de reserva y pago en línea con WeTravel.
 *
 * WeTravel da un botón + script (embed_checkout.js), pero ese script solo busca
 * los botones una vez al cargar la página (DOMContentLoaded). En esta SPA la
 * página del tour se dibuja después, así que el script nunca vería el botón.
 * Por eso replicamos aquí lo que hace: abrir el checkout en un iframe a
 * pantalla completa y cerrarlo cuando WeTravel avisa con "wtrvlCheckoutClosed".
 */
const WETRAVEL_ENV = 'https://pacifictourscr.wetravel.com';

const urlCheckout = (uuid) =>
  `${WETRAVEL_ENV}/checkout_embed?uuid=${encodeURIComponent(uuid)}&source=widget_book_now`;

const ESTILO_IFRAME = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  width: '100vw', height: '100dvh', border: 0,
  zIndex: 2147483000, background: '#fff',
};

function BotonWeTravel({ uuid, texto, className }) {
  const [abierto, setAbierto] = useState(false);
  const iframeRef = useRef(null);

  const cerrar = useCallback(() => setAbierto(false), []);

  // Bloquea el scroll de la página mientras el checkout está abierto.
  useEffect(() => {
    if (!abierto) return;
    const html = document.documentElement;
    const prevBody = document.body.style.overflow;
    const prevHtml = html.style.overflow;
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, [abierto]);

  // Mensajes que manda el checkout de WeTravel.
  useEffect(() => {
    if (!abierto) return;
    const esWeTravel = (origin) => {
      try {
        const host = new URL(origin).hostname;
        return host === 'wetravel.com' || host.endsWith('.wetravel.com');
      } catch {
        return false;
      }
    };
    const onMessage = (e) => {
      if (esWeTravel(e.origin) && e.data === 'wtrvlCheckoutClosed') cerrar();
    };
    const onKey = (e) => e.key === 'Escape' && cerrar();
    window.addEventListener('message', onMessage);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierto, cerrar]);

  // Igual que el script oficial: avisa al iframe que viene de un "button widget".
  const onIframeLoad = () => {
    setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage('buttonWidget', '*');
    }, 3000);
  };

  if (!uuid) return null;

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          registrarEvento('begin_checkout', { tour_uuid: uuid, metodo: 'wetravel' });
          setAbierto(true);
        }}
      >
        {texto}
      </button>

      {abierto && createPortal(
        <iframe
          ref={iframeRef}
          className="wtrvl-ifrm"
          title="WeTravel checkout"
          src={urlCheckout(uuid)}
          allow="payment"
          style={ESTILO_IFRAME}
          onLoad={onIframeLoad}
        />,
        document.body
      )}
    </>
  );
}

export default BotonWeTravel;
